import { useState } from 'react'
import cx from 'classnames'

import Styles from './IntegrationsDropdown.module.scss'
import { Button } from 'blaze-ui/Button'
import { ChevronDown2, UserProfileAdd2 } from 'blaze-ui/icons'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationItem } from 'components/ExternalIntegrationSelect/ExternalIntegrationItem'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { IntegrationRequiredModal } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { SOCIAL_POST_PLATFORMS } from 'components/ScheduledPostTargetsPreview/ScheduledPostTargetsPreview'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import Avatar from 'common/Avatar/Avatar'
import { getInitials } from 'utils'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'

interface IntegrationsDropdownProps {
  postTo: number[]
  setPostTo: (postTo: number[]) => void
}

const MAX_VISIBLE_INTEGRATIONS = 3

// Overlapping avatars (each badged with its platform icon) plus a "+N" pill for the rest,
// shown inside the trigger button to preview which accounts the duplicate will post to.
const SelectedIntegrationsPreview = ({ integrations }: { integrations: ExternalIntegration[] }) => {
  const visible = integrations.slice(0, MAX_VISIBLE_INTEGRATIONS)
  const overflow = integrations.length - visible.length

  return (
    <div className={Styles.avatarStack}>
      {visible.map((integration) => {
        const { avatar, name } = extractProfileInfo(integration)

        return (
          <div key={integration.id} className={Styles.avatarChip}>
            <Avatar
              size={24}
              label={getInitials(name)}
              url={avatar || Silhouette}
              altText={name}
              className={Styles.avatar}
            />
            <span className={Styles.platformBadge}>
              <PlatformIcon size={14} platform={integration.provider} />
            </span>
          </div>
        )
      })}
      {overflow > 0 && <div className={Styles.overflowChip}>+{overflow}</div>}
    </div>
  )
}

export const IntegrationsDropdown = ({ postTo, setPostTo }: IntegrationsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { data } = useExternalIntegrations()
  const { openModal } = useModals()
  const integrations = data?.external_integrations ?? []

  const handleToggle = (integrationId: number) => {
    if (postTo.includes(integrationId)) {
      setPostTo(postTo.filter((id) => id !== integrationId))
    } else {
      setPostTo([...postTo, integrationId])
    }
  }

  const handleConnect = (provider: ExternalIntegrationEnum) => {
    openModal(IntegrationRequiredModal, { integration: provider }, { newStack: true })
  }

  const hasIntegrations = integrations.length > 0
  const selectedIntegrations = integrations.filter((integration) => postTo.includes(integration.id))

  // Platforms that are not yet connected
  const unconnectedPlatforms = SOCIAL_POST_PLATFORMS.filter(
    (platform) => !integrations.some((integration) => integration.provider === platform),
  )

  const renderButtonContent = () => {
    if (!hasIntegrations) {
      return 'Connect Accounts'
    }

    if (selectedIntegrations.length === 0) {
      return (
        <Text variant="secondary" color="var(--dark-40)">
          Select accounts
        </Text>
      )
    }

    return <SelectedIntegrationsPreview integrations={selectedIntegrations} />
  }

  return (
    <DropdownTrigger onOpenChange={setIsOpen} placement="bottom-start">
      <Button
        variant="secondary"
        frontIcon={() => {
          if (!hasIntegrations) {
            return <UserProfileAdd2 strokeWidth="1.15" />
          }

          return null
        }}
        endIcon={() => {
          if (!hasIntegrations) {
            return null
          }

          return (
            <div className={cx({ [Styles.reverse]: isOpen })}>
              <ChevronDown2 strokeWidth="1.15" />
            </div>
          )
        }}
        size="lg"
      >
        {renderButtonContent()}
      </Button>
      {(close) => (
        <div className={Styles.list}>
          {integrations.map((integration) => (
            <div key={integration.id} className={Styles.row}>
              <ExternalIntegrationItem externalIntegration={integration} renderPlatformIcon />
              <ToggleButton
                checked={postTo.includes(integration.id)}
                name={`integration-${integration.id}`}
                handleChange={() => handleToggle(integration.id)}
                size="small"
              />
            </div>
          ))}
          {unconnectedPlatforms.map((platform) => (
            <button
              key={platform}
              className={Styles.connectRow}
              onClick={() => {
                handleConnect(platform)
                close()
              }}
            >
              <PlatformIcon platform={platform} size={16} />
              <Text variant="secondary">Connect {integrationToName[platform]}</Text>
            </button>
          ))}
        </div>
      )}
    </DropdownTrigger>
  )
}
